import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HomeworkGuyLogo from './HomeworkGuyLogo';
import OptimizedImage from './OptimizedImage';
import { faTimes, faBars, faGraduationCap, faBook, faCalculator, faFlask, faCode, faChartLine, faLanguage, faHistory, faPencilAlt, faChevronRight, faChalkboardTeacher, faLaptop, faFilePdf, faTv, faDatabase, faFileInvoiceDollar, faClock, faBookOpen, faSearch, faDesktop, faPen, faSquareRootAlt, faChartBar, faAtom, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import '../styles/Navigation.scss';
import { BASE_URL } from '../utils/SEOUtils';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('/');
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  
  // Check if we're on the Order page
  const isOrderPage = location.pathname === '/order' || location.pathname === 'order';

  // Update active link when location changes
  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  // Handle scroll effect for sticky header
  useEffect(() => {
    // If on Order page, always set scrolled to true
    if (isOrderPage) {
      setScrolled(true);
      return;
    }
    
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Initial call to set the correct state on mount
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOrderPage]); // Add isOrderPage as a dependency

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu when location changes and handle navigation cleanup
  useEffect(() => {
    const cleanup = () => {
      document.body.classList.remove('order-page-active');
      document.body.classList.remove('textarea-focused');
      document.body.classList.remove('no-scroll');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, 0);
    };

    // Close mobile menu on route change
    setIsOpen(false);
    setMobileServicesOpen(false);
    
    // Close services dropdown menu on route change
    setShowServicesDropdown(false);
    
    // Handle navigation cleanup when route changes
    if (!isOrderPage) {
      cleanup();
    }
  }, [location, isOrderPage]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setMobileServicesOpen(false); // Close services when toggling main menu
    // Prevent body scrolling when menu is open
    document.body.style.overflow = !isOpen ? 'hidden' : '';
  };

  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const closeMobileMenu = () => {
    setIsOpen(false);
    setMobileServicesOpen(false);
    document.body.style.overflow = '';
  };
  
  // Force navigation - only used from Order page
  const handleDirectNavigation = (path: string, e: React.MouseEvent) => {
    if (isOrderPage) {
      e.preventDefault();
      
      // Force cleanup of Order page classes
      document.body.classList.remove('order-page-active');
      document.body.classList.remove('textarea-focused');
      document.body.classList.remove('no-scroll');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Reset scroll position
      window.scrollTo(0, 0);
      
      // Create navigation path with clean URLs
      const baseUrl = window.location.origin;
      // If path is '/' set it to empty string for the correct root URL
      const cleanPath = path === '/' ? '' : path.startsWith('/') ? path.substring(1) : path;
      const targetPath = `${baseUrl}/${cleanPath}`;
      
      // Use window.location for most reliable navigation from Order page
      window.location.href = targetPath;
      
      return true;
    }
    
    // For non-Order pages, just close mobile menu and let React Router handle navigation
    closeMobileMenu();
    return false;
  };

  // Handle normal navigation (non-Order page)
  const handleNavigation = () => {
    closeMobileMenu();
  };

  // Enhanced navigation handler
  const handleLinkClick = (path: string, e: React.MouseEvent) => {
    if (isOrderPage) {
      return handleDirectNavigation(path, e);
    }
    
    // For non-Order pages, just close mobile menu and let React Router handle it
    closeMobileMenu();
  };

  // Define service items with icons
  const serviceItems = [
    {
      title: "Someone To Take My Online Class",
      link: "/someone-to-take-my-online-class",
      icon: faLaptop
    },
    {
      title: "College Essay Writing",
      link: "/college-essay-writing",
      icon: faPen
    },
    {
      title: "Dissertation Writing",
      link: "/dissertation-writing-service",
      icon: faBook
    },
    {
      title: "Math Assignment Help",
      link: "/math-assignment-help-service",
      icon: faCalculator
    },
    {
      title: "Programming Assignment Help",
      link: "/programming-assignment-help-service",
      icon: faCode
    },
    {
      title: "Research Paper Writing",
      link: "/research-paper-writing-service",
      icon: faSearch
    },
    {
      title: "PowerPoint Presentation",
      link: "/powerpoint-presentation-service",
      icon: faDesktop
    },
    {
      title: "Statistics Class Help",
      link: "/statistics-class-helper",
      icon: faChartBar
    },
    {
      title: "Database Management Help",
      link: "/database-management-service",
      icon: faDatabase
    },
    {
      title: "Accounting Class Help",
      link: "/affordable-accounting-class-help",
      icon: faCalculator
    },
    {
      title: "Chemistry Online Class",
      link: "/chemistry-online-class-taker",
      icon: faFlask
    },
    {
      title: "Physics Class Help",
      link: "/physics-class-help-service",
      icon: faAtom
    },
    {
      title: "Urgent Essay Writing Help",
      link: "/urgent-essay-writing-help",
      icon: faClock
    },
    {
      title: "24/7 Homework Help Service",
      link: "/homework-help-service",
      icon: faBookOpen
    },
    {
      title: "Hire Someone To Take My Exam",
      link: "/hire-someone-to-take-my-exam",
      icon: faGraduationCap
    }
  ];

  // Check if current path is a service page
  const isServicePage = serviceItems.some(service => service.link === location.pathname) || location.pathname === '/services';

  // Simple path formatter - no special handling needed with BrowserRouter
  const getFormattedPath = (path: string) => {
    return path;
  };

  return (
    <>
      <style>
        {`
          @media (min-width: 992px) {
            #services-dropdown:hover .dropdown-menu {
              display: block;
              margin-top: 0 !important;
              top: 100% !important;
              border-top: none;
              transform: translateY(0);
              position: absolute;
              z-index: 1030;
            }
            .dropdown-menu {
              margin-top: 0;
            }
            .navbar-nav .nav-item.dropdown:hover .dropdown-menu {
              margin-top: 0 !important;
            }
          }
        `}
      </style>
      <Navbar 
        collapseOnSelect 
        expand="lg" 
        variant="light" 
        className={`main-navigation ${scrolled ? 'scrolled' : ''} ${isOpen ? 'menu-open' : ''}`}
        sticky="top"
      >
        <Container>
          <Navbar.Brand>
            <Link
              to="/"
              className="navbar-brand"
              onClick={(e) => handleLinkClick('/', e)}
              aria-label="Homework Guy Logo - Homepage"
            >
              <HomeworkGuyLogo />
            </Link>
          </Navbar.Brand>
          
          <Navbar.Toggle 
            aria-controls="responsive-navbar-nav" 
            className="d-lg-none"
            onClick={toggleMenu}
          >
            <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
          </Navbar.Toggle>
          
          {/* Desktop Navigation */}
          <Navbar.Collapse id="responsive-navbar-nav" className={`d-none d-lg-flex ${isOpen ? 'show' : ''}`}>
            <Nav className="ms-auto">
              <Nav.Item>
                <Link
                  to="/"
                  className={`nav-link ${activeLink === '/' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/', e)}
                >
                  Home
                </Link>
              </Nav.Item>
              
              <NavDropdown
                title="Services"
                id="collapsible-nav-dropdown"
                show={showServicesDropdown}
                onMouseEnter={() => setShowServicesDropdown(true)}
                onMouseLeave={() => setShowServicesDropdown(false)}
                onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                className={`services-dropdown ${isServicePage ? 'active' : ''}`}
              >
                <div className={`services-grid ${isMobile ? 'mobile-services-grid' : ''}`}>
                  {serviceItems.map((service, index) => (
                    <Link
                      key={index}
                      to={getFormattedPath(service.link)}
                      className="service-item"
                      onClick={(e) => handleLinkClick(service.link, e)}
                    >
                      {service.title}
                    </Link>
                  ))}
                  <Link
                    to="/services"
                    className="view-all-services"
                    onClick={(e) => handleLinkClick('/services', e)}
                  >
                    View All Services <FontAwesomeIcon icon={faChevronRight} />
                  </Link>
                </div>
              </NavDropdown>
              
              <Nav.Item>
                <Link
                  to="/how-it-works"
                  className={`nav-link ${activeLink === '/how-it-works' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/how-it-works', e)}
                >
                  How It Works
                </Link>
              </Nav.Item>
              
              <Nav.Item>
                <Link
                  to="/blog"
                  className={`nav-link ${activeLink === '/blog' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/blog', e)}
                >
                  Blog
                </Link>
              </Nav.Item>
              
              <Nav.Item>
                <Link
                  to="/contact"
                  className={`nav-link ${activeLink === '/contact' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/contact', e)}
                >
                  Contact
                </Link>
              </Nav.Item>
              
              <Nav.Item className="order-nav-item">
                <Link
                  to="/order"
                  className="nav-link order-link"
                  onClick={(e) => handleLinkClick('/order', e)}
                >
                  Order Now
                </Link>
              </Nav.Item>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Mobile Side Shutter */}
      {isMobile && (
        <>
          {/* Overlay */}
          <div 
            className={`mobile-overlay ${isOpen ? 'show' : ''}`}
            onClick={closeMobileMenu}
          />
          
          {/* Side Shutter */}
          <div className={`mobile-side-shutter ${isOpen ? 'open' : ''}`}>
            <div className="mobile-shutter-header">
              <button 
                className="mobile-close-btn"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="mobile-shutter-content">
              <nav className="mobile-nav">
                <Link
                  to="/"
                  className={`mobile-nav-link ${activeLink === '/' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/', e)}
                >
                  Home
                </Link>
                
                <div className="mobile-services-section">
                  <button 
                    className={`mobile-services-toggle ${mobileServicesOpen ? 'open' : ''}`}
                    onClick={toggleMobileServices}
                  >
                    Services
                    <FontAwesomeIcon icon={faChevronDown} className="chevron-icon" />
                  </button>
                  
                  <div className={`mobile-services-list ${mobileServicesOpen ? 'open' : ''}`}>
                    {serviceItems.map((service, index) => (
                      <Link
                        key={index}
                        to={getFormattedPath(service.link)}
                        className="mobile-service-item"
                        onClick={(e) => handleLinkClick(service.link, e)}
                      >
                        <FontAwesomeIcon icon={service.icon} className="service-icon" />
                        {service.title}
                      </Link>
                    ))}
                    <Link
                      to="/services"
                      className="mobile-view-all-services"
                      onClick={(e) => handleLinkClick('/services', e)}
                    >
                      <FontAwesomeIcon icon={faChevronRight} className="service-icon" />
                      View All Services
                    </Link>
                  </div>
                </div>
                
                <Link
                  to="/how-it-works"
                  className={`mobile-nav-link ${activeLink === '/how-it-works' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/how-it-works', e)}
                >
                  How It Works
                </Link>
                
                <Link
                  to="/blog"
                  className={`mobile-nav-link ${activeLink === '/blog' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/blog', e)}
                >
                  Blog
                </Link>
                
                <Link
                  to="/contact"
                  className={`mobile-nav-link ${activeLink === '/contact' ? 'active' : ''}`}
                  onClick={(e) => handleLinkClick('/contact', e)}
                >
                  Contact
                </Link>
                
                <Link
                  to="/order"
                  className="mobile-order-btn"
                  onClick={(e) => handleLinkClick('/order', e)}
                >
                  Order Now
                </Link>
              </nav>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navigation; 